import React, { useState, useEffect, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { submitFormSession, firstIncompleteStepIndex, getFormSessionErrorMessage } from '../utils/formSessionApi';
import {
  applyDemoSellerEmails,
  applyListingWizardDefaults,
  firstVisibleListingStepIndex,
  isDemoActorEmail,
  lastVisibleListingStep,
  LISTING_QUESTION_SKIP_IF,
  nextVisibleListingStep,
  visibleListingStepLabel,
} from '../utils/listingWizardDefaults';
import './ContractWizard.css';
import './ListingWizard.css';
import { getListingsApiUrl, getContractApiUrl } from '../utils/apiUrls';
import { FORMS_API_URL } from '../utils/formSessionApi';
import { trackFormWizardEvent, formWizardEventPayload } from '../utils/formWizardTelemetry';
import {
  wizardBlockedReason,
  WIZARD_ANSWER_QUESTION_REASON,
  WIZARD_PRIMARY_BUTTON_CLASS,
} from '../utils/wizardContinue';
import WizardProgress from './WizardProgress';
import WizardStepReady from './WizardStepReady';
import PropertyAddressFields from './PropertyAddressFields';
import PhoneNumberInput from './PhoneNumberInput';

const LISTINGS_URL = getListingsApiUrl();
const BACKEND_URL = getContractApiUrl();


const today = () => new Date().toISOString().split('T')[0];
const inDays = (n) => {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return d.toISOString().split('T')[0];
};
// Format YYYY-MM-DD → MM/DD/YYYY for display
const toMMDDYYYY = (isoStr) => {
  if (!isoStr) return '';
  const [y, m, d] = isoStr.split('-');
  return `${m}/${d}/${y}`;
};

const LISTING_STEP_INDEX = {
  address: 0,
  legal_description: 1,
  listing_price: 2,
  mls_number: 3,
  year_built: 4,
  expiration_date: 5,
  commission_structure: 6,
  commission_percent: 7,
  commission_amount: 8,
  disclose_buyer_broker_comp: 9,
  protection_period_days: 10,
  lease_period_days: 11,
  additional_inclusions: 12,
  exclusions: 13,
  lock_box_authorized: 14,
  disclose_offers: 15,
  purchase_rsa: 16,
  rsa_cost: 17,
  flood_hazard: 18,
  additional_provisions: 19,
  seller: 20,
};

const EMPTY_INITIAL_VALUES = Object.freeze({});

// ---------------------------------------------------------------------------
const ListingWizard = ({
  isOpen,
  onClose,
  onBackToCatalog,
  mode = 'modal',
  initialValues = EMPTY_INITIAL_VALUES,
  wizardSteps = [],
  formSessionToken = null,
  initialSubmitResult = null,
}) => {
  const { user: auth0User, getAccessTokenSilently } = useAuth0();
  const isPageMode = mode === 'page';
  const visible = isPageMode || isOpen;

  const [currentStep, setCurrentStep] = useState(0);
  const [data, setData] = useState({});
  const [sellers, setSellers] = useState([{ name: '', email: '', phone: '' }]);
  const [propertyEnriching, setPropertyEnriching] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);
  const [submitPhase, setSubmitPhase] = useState(null);
  const [stepSeeded, setStepSeeded] = useState(false);
  const wizardOpenedRef = useRef(false);
  const initialValuesRef = useRef(initialValues);
  const wizardStepsRef = useRef(wizardSteps);
  initialValuesRef.current = initialValues;
  wizardStepsRef.current = wizardSteps;

  // Seed once when the wizard opens; avoid clearing email prefill on re-renders.
  useEffect(() => {
    if (!visible) {
      wizardOpenedRef.current = false;
      return;
    }
    if (wizardOpenedRef.current) return;
    wizardOpenedRef.current = true;
    if (!formSessionToken && auth0User?.email) {
      trackFormWizardEvent(getAccessTokenSilently, formWizardEventPayload({
        phase: 'started',
        formType: 'listing_agreement',
        userEmail: auth0User.email,
        userName: auth0User.name || auth0User.nickname || '',
      })).catch(() => {});
    }

    setIsSubmitting(false);
    setSubmitResult(initialSubmitResult || null);
    setSubmitPhase(null);

    const seeded = applyListingWizardDefaults({ ...(initialValuesRef.current || {}) });
    setData(seeded);
    const demoActor = isDemoActorEmail(auth0User?.email);
    let nextSellers = [{ name: '', email: '', phone: '' }];
    if (Array.isArray(seeded.sellers) && seeded.sellers.length) {
      nextSellers = seeded.sellers.map((s) => ({
        name: s?.name || '',
        email: s?.email || '',
        phone: s?.phone || '',
      }));
    } else if (seeded.seller_name) {
      nextSellers = [{ name: seeded.seller_name, email: '', phone: '' }];
    }
    setSellers(demoActor ? applyDemoSellerEmails(nextSellers) : nextSellers);

    let startStep = 0;
    const steps = wizardStepsRef.current;
    if (steps?.length) {
      const stepIdx = firstIncompleteStepIndex(steps, seeded);
      const stepDef = steps[stepIdx];
      startStep = LISTING_STEP_INDEX[stepDef?.step_id] ?? stepIdx;
    }
    const skipAwareQuestions = Object.keys(LISTING_STEP_INDEX).map((id) => ({
      id,
      skipIf: LISTING_QUESTION_SKIP_IF[id],
    }));
    startStep = firstVisibleListingStepIndex(skipAwareQuestions, startStep, seeded);
    setCurrentStep(startStep);
    setStepSeeded(true);
  }, [visible]);

  // ---------------------------------------------------------------------------
  // Questions
  // ---------------------------------------------------------------------------
  const questions = [
    {
      id: 'address',
      question: 'What is the property address?',
      type: 'address',
      required: true,
      validation: () => (data.street_address || '').trim().length > 0,
    },
    {
      id: 'legal_description',
      question: 'What is the legal description of the property?',
      type: 'textarea',
      placeholder: 'Lot, block, subdivision, plat book/page, metes and bounds, etc.',
      required: true,
      validation: () => (data.legal_description || '').trim().length > 0,
    },
    {
      id: 'listing_price',
      question: 'What is the listing price?',
      type: 'currency',
      placeholder: '0',
      required: true,
      validation: () => parseFloat(data.listing_price) > 0,
    },
    {
      id: 'mls_number',
      question: 'What is the MLS number?',
      type: 'optional_text',
      placeholder: 'e.g. #426111693',
      required: false,
      skipLabel: 'Skip — no MLS yet',
    },
    {
      id: 'year_built',
      question: 'What year was the property built?',
      type: 'optional_number',
      placeholder: 'e.g. 1998',
      required: false,
      skipLabel: 'Skip',
    },
    {
      id: 'expiration_date',
      question: `When does the listing agreement expire?\nThe standard is 90 days from today (${toMMDDYYYY(inDays(90))}).`,
      type: 'expiration',
      required: true,
      validation: () => (data.expiration_date || '').length > 0,
    },
    {
      id: 'commission_structure',
      question: 'Standard % commission structure or fixed fee?',
      type: 'commission_structure',
      required: true,
      validation: () => data.commission_structure === 'percent' || data.commission_structure === 'fixed',
    },
    {
      id: 'commission_percent',
      question: 'What is the listing-side commission percentage?',
      type: 'commission',
      required: true,
      skipIf: LISTING_QUESTION_SKIP_IF.commission_percent,
      validation: () => parseFloat(data.commission_percent) > 0,
    },
    {
      id: 'commission_amount',
      question: 'What is the listing-side fixed commission fee?',
      type: 'currency',
      placeholder: '0',
      required: true,
      skipIf: LISTING_QUESTION_SKIP_IF.commission_amount,
      validation: () => parseFloat(data.commission_amount) > 0,
    },
    {
      id: 'disclose_buyer_broker_comp',
      question: 'Does the Seller authorize Broker to disclose the amount of compensation to be offered to the Buyer’s Broker in marketing and advertising the property?',
      type: 'yes_no',
      required: true,
      validation: () => data.disclose_buyer_broker_comp === 'yes' || data.disclose_buyer_broker_comp === 'no',
    },
    {
      id: 'protection_period_days',
      question: 'The compensation shall be due if there is a sale or exchange of the Property within how many days after the termination of this Agreement, if with anyone to whom Broker has shown the Property, or with whom the Broker has negotiated concerning the Property prior to the termination of this Agreement?',
      type: 'days',
      required: true,
      validation: () => parseInt(data.protection_period_days, 10) > 0,
    },
    {
      id: 'lease_period_days',
      question: 'The compensation shall be due if there is a sale or exchange of the Property during, or after, the term of this Agreement to any party to whom the Property is rented or leased during the term of this Agreement, or within how many days after?',
      type: 'days',
      required: true,
      validation: () => parseInt(data.lease_period_days, 10) > 0,
    },
    {
      id: 'additional_inclusions',
      question: 'Additional Inclusions. The following items shall also remain with the Property at no additional cost to Buyer:',
      type: 'optional_textarea',
      placeholder: 'Items that stay with the property',
      skipLabel: 'Skip — none',
      required: false,
    },
    {
      id: 'exclusions',
      question: 'Exclusions. The following items shall not remain with the Property:',
      type: 'optional_textarea',
      placeholder: 'Items the seller will take',
      skipLabel: 'Skip — none',
      required: false,
    },
    {
      id: 'lock_box_authorized',
      question: 'Is the Broker authorized to place a “Lock Box” on the Property?',
      type: 'yes_no',
      required: true,
      validation: () => data.lock_box_authorized === 'yes' || data.lock_box_authorized === 'no',
    },
    {
      id: 'disclose_offers',
      question: 'Does the Seller authorize Broker to disclose the existence of offers on the property?',
      type: 'yes_no',
      required: true,
      validation: () => data.disclose_offers === 'yes' || data.disclose_offers === 'no',
    },
    {
      id: 'purchase_rsa',
      question: 'Will the Seller purchase an RSA at this time?',
      type: 'yes_no',
      required: true,
      validation: () => data.purchase_rsa === 'yes' || data.purchase_rsa === 'no',
    },
    {
      id: 'rsa_cost',
      question: 'If known, the cost of an RSA is approximately $',
      type: 'optional_currency',
      placeholder: '0',
      skipLabel: 'Skip — unknown',
      required: false,
      skipIf: LISTING_QUESTION_SKIP_IF.rsa_cost,
    },
    {
      id: 'flood_hazard',
      question: 'Has the Seller been notified by any city or county governmental agency, or is the Seller aware, that the Property is in a flood hazard area?',
      type: 'yes_no',
      required: true,
      validation: () => data.flood_hazard === 'yes' || data.flood_hazard === 'no',
    },
    {
      id: 'additional_provisions',
      question: 'Additional Provisions:',
      type: 'optional_textarea',
      placeholder: 'Any additional listing terms',
      skipLabel: 'Skip — none',
      required: false,
    },
    {
      id: 'seller',
      question: 'Who is the seller?',
      type: 'seller',
      required: true,
      validation: () => (sellers[0]?.name || '').trim().length > 0,
    },
  ];

  const set = (field, value) => setData(prev => ({ ...prev, [field]: value }));

  const updateSeller = (idx, field, value) => {
    setSellers(prev => prev.map((s, i) => i === idx ? { ...s, [field]: value } : s));
  };
  const addSeller = () => {
    if (sellers.length < 3) setSellers(prev => [...prev, { name: '', email: '', phone: '' }]);
  };
  const removeSeller = (idx) => {
    setSellers(prev => prev.filter((_, i) => i !== idx));
  };

  // ---------------------------------------------------------------------------
  // Navigation
  // ---------------------------------------------------------------------------
  const currentQuestion = questions[currentStep];
  const isLastStep = currentStep === lastVisibleListingStep(questions, data);

  const canProceed = () => {
    if (!currentQuestion.required) return true;
    if (currentQuestion.validation) return currentQuestion.validation();
    return !!(data[currentQuestion.id]);
  };

  const listingContinueBlockedReason = () => {
    if (!currentQuestion.required || canProceed()) return '';
    if (currentQuestion.id === 'address') {
      return 'Enter the property street address to continue.';
    }
    if (currentQuestion.id === 'seller') {
      return 'Enter the seller name to continue.';
    }
    if (currentQuestion.type === 'yes_no' || currentQuestion.type === 'commission_structure') {
      return wizardBlockedReason({
        ready: false,
        verb: 'Select',
        missingLabels: currentQuestion.type === 'commission_structure'
          ? ['a commission structure']
          : [],
        fallback: WIZARD_ANSWER_QUESTION_REASON,
      });
    }
    return wizardBlockedReason({ ready: false, fallback: WIZARD_ANSWER_QUESTION_REASON });
  };

  const advanceFrom = (from, values = data) => {
    const next = nextVisibleListingStep(questions, from, values, 1);
    if (next !== from) setCurrentStep(next);
  };

  const enrichPropertyAndAdvance = async () => {
    setPropertyEnriching(true);
    try {
      const payload = {
        user_email: auth0User?.email,
        property_address: data.street_address,
        property_address_city: data.city,
        property_address_state: data.state,
        property_address_zip_code: data.zip_code,
      };
      const url = formSessionToken
        ? `${FORMS_API_URL}/sessions/${formSessionToken}/enrich-property`
        : `${BACKEND_URL}/wizard/enrich-property`;
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const body = await res.json();
      const enriched = body.enrichment;
      setData((prev) => {
        const updated = { ...prev };
        if (!res.ok || !enriched) {
          return updated;
        }
        const copies = [
          ['city', 'city'],
          ['state', 'state'],
          ['zip_code', 'zip_code'],
          ['county', 'county'],
          ['legal_description', 'legal_description'],
          ['parcel_id', 'parcel_id'],
          ['mls_number', 'mls_number'],
          ['year_built', 'year_built'],
          ['listing_price', 'listing_price'],
          ['flood_hazard', 'flood_hazard'],
          ['property_features', 'property_features'],
          ['additional_inclusions', 'additional_inclusions'],
          ['seller_name', 'seller_name'],
        ];
        copies.forEach(([src, dest]) => {
          if (enriched[src] && !updated[dest]) {
            updated[dest] = enriched[src];
          }
        });
        if (enriched.property_address_city && !updated.city) {
          updated.city = enriched.property_address_city;
        }
        if (enriched.property_address_state && !updated.state) {
          updated.state = enriched.property_address_state;
        }
        if (enriched.property_address_zip_code && !updated.zip_code) {
          updated.zip_code = enriched.property_address_zip_code;
        }
        if (enriched.property_address_county && !updated.county) {
          updated.county = enriched.property_address_county;
        }
        return updated;
      });
      if (enriched?.seller_name && !sellers[0]?.name) {
        setSellers((prev) => {
          const next = [...prev];
          next[0] = { ...next[0], name: enriched.seller_name };
          return next;
        });
      }
    } catch (err) {
      console.error('Listing property enrichment failed:', err);
    } finally {
      setPropertyEnriching(false);
      advanceFrom(currentStep);
    }
  };

  const handleNext = () => {
    if (currentQuestion.required && !canProceed()) {
      return;
    }
    if (currentQuestion.id === 'purchase_rsa' && data.purchase_rsa === 'no') {
      set('rsa_cost', '');
    }
    if (currentQuestion.id === 'address' && !isLastStep) {
      enrichPropertyAndAdvance();
      return;
    }
    if (!isLastStep) advanceFrom(currentStep);
  };

  const handlePrevious = () => {
    if (currentStep === 0 && onBackToCatalog) {
      onBackToCatalog();
      return;
    }
    const previous = nextVisibleListingStep(questions, currentStep, data, -1);
    if (previous !== currentStep) setCurrentStep(previous);
  };

  const handleAutoAdvance = (overrides = {}) => {
    const values = { ...data, ...overrides };
    if (values.purchase_rsa === 'no') {
      set('rsa_cost', '');
    }
    advanceFrom(currentStep, values);
  };

  // ---------------------------------------------------------------------------
  // Submit — creates listing in KeyFlo (SkySlope sync runs after LA is fully executed)
  // ---------------------------------------------------------------------------
  const handleSubmit = async () => {
    if (currentQuestion.required && !canProceed()) return;
    setIsSubmitting(true);
    setSubmitPhase('creating');

    try {
      // Build parties from sellers array
      const submitSellers = isDemoActorEmail(auth0User?.email)
        ? applyDemoSellerEmails(sellers)
        : sellers;
      const parties = submitSellers
        .filter(s => (s.name || '').trim())
        .map((s, idx) => ({
          role: idx === 0 ? 'seller' : 'co_seller',
          name: s.name.trim(),
          email: s.email || null,
          phone: s.phone ? s.phone.replace(/\D/g, '').slice(0, 10) || null : null,
        }));

      const useFixed = data.commission_structure === 'fixed';
      const commPct = parseFloat(data.commission_percent || 3);
      const commFraction = commPct / 100;

      const payload = {
        user_email: auth0User?.email,
        street_address: (data.street_address || '').trim(),
        city: (data.city || '').trim() || null,
        state: (data.state || '').trim() || null,
        zip_code: (data.zip_code || '').trim() || null,
        listing_price: parseFloat(data.listing_price) || null,
        listing_date: today(),
        expiration_date: data.expiration_date || inDays(90),
        mls_number: (data.mls_number || '').trim() || null,
        year_built: data.year_built ? parseInt(data.year_built) : null,
        commission_structure: useFixed ? 'fixed' : 'percent',
        commission_percent: useFixed ? null : commFraction,
        commission_amount: useFixed ? (parseFloat(data.commission_amount) || null) : null,
        legal_description: (data.legal_description || '').trim() || null,
        parcel_id: (data.parcel_id || '').trim() || null,
        county: (data.county || '').trim() || null,
        disclose_buyer_broker_comp: data.disclose_buyer_broker_comp || null,
        protection_period_days: parseInt(data.protection_period_days, 10) || 30,
        lease_period_days: parseInt(data.lease_period_days, 10) || 30,
        additional_inclusions: (data.additional_inclusions || '').trim() || null,
        exclusions: (data.exclusions || '').trim() || null,
        lock_box_authorized: data.lock_box_authorized || null,
        disclose_offers: data.disclose_offers || null,
        purchase_rsa: data.purchase_rsa || null,
        flood_hazard: data.flood_hazard || null,
        rsa_cost: data.purchase_rsa === 'no' || !data.rsa_cost ? null : parseFloat(data.rsa_cost),
        additional_provisions: (data.additional_provisions || '').trim() || null,
        parties,
        sellers: submitSellers,
      };

      if (formSessionToken) {
        const sessionResult = await submitFormSession(formSessionToken, payload);
        setSubmitPhase('done');
        setSubmitResult({
          success: true,
          listingId: sessionResult.entity_id,
          address: `${payload.street_address}${payload.city ? `, ${payload.city}` : ''}`,
          syncMessage: 'Listing created. Check your email for confirmation.',
        });
        return;
      }

      // Step 1 — create in KeyFlo
      const createResp = await fetch(`${LISTINGS_URL}/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const createData = await createResp.json();

      if (!createResp.ok) {
        setSubmitResult({ success: false, message: createData.error || 'Failed to create listing' });
        return;
      }

      const listingId = createData.id;

      setSubmitPhase('done');
      setSubmitResult({
        success: true,
        listingId,
        address: `${payload.street_address}${payload.city ? `, ${payload.city}` : ''}`,
        syncMessage:
          'Listing saved. Check your email for the listing confirmation with the completed agreement attached.',
      });
      if (auth0User?.email) {
        trackFormWizardEvent(getAccessTokenSilently, formWizardEventPayload({
          phase: 'completed',
          formType: 'listing_agreement',
          userEmail: auth0User.email,
          userName: auth0User.name || auth0User.nickname || '',
          metadata: {
            listing_id: listingId,
            property_address: payload.street_address,
          },
        })).catch(() => {});
      }

    } catch (err) {
      setSubmitResult({
        success: false,
        message: formSessionToken
          ? getFormSessionErrorMessage(err, 'Failed to create listing')
          : `Network error: ${err.message || 'Unknown error'}`,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Input renderers
  // ---------------------------------------------------------------------------
  const renderInput = () => {
    const q = currentQuestion;

    switch (q.type) {
      case 'address':
        return (
          <PropertyAddressFields
            values={data}
            onChange={set}
            streetKey="street_address"
            cityKey="city"
            stateKey="state"
            zipKey="zip_code"
          />
        );

      case 'currency':
        return (
          <div className="wizard-currency">
            <span className="currency-symbol">$</span>
            <input
              type="number"
              value={data.listing_price || ''}
              onChange={e => set('listing_price', e.target.value)}
              placeholder={q.placeholder}
              className="wizard-input currency-input"
              autoFocus
            />
          </div>
        );

      case 'textarea':
        return (
          <textarea
            value={data[q.id] || ''}
            onChange={e => set(q.id, e.target.value)}
            placeholder={q.placeholder}
            className="wizard-textarea"
            rows={4}
            autoFocus
          />
        );

      case 'optional_textarea':
        return (
          <div className="lw-optional">
            <textarea
              value={data[q.id] || ''}
              onChange={e => set(q.id, e.target.value)}
              placeholder={q.placeholder}
              className="wizard-textarea"
              rows={4}
              autoFocus
            />
            <button
              className="wizard-button lw-skip-btn"
              onClick={() => {
                set(q.id, '');
                handleAutoAdvance();
              }}
            >
              {q.skipLabel}
            </button>
          </div>
        );

      case 'commission_structure':
        return (
          <div className="lw-commission">
            {[
              { value: 'percent', label: 'Standard %' },
              { value: 'fixed', label: 'Fixed fee' },
            ].map((choice) => (
              <button
                key={choice.value}
                className={`wizard-button ${data.commission_structure === choice.value ? 'active' : ''}`}
                onClick={() => {
                  const next = {
                    commission_structure: choice.value,
                    commission_percent: choice.value === 'fixed' ? '' : data.commission_percent,
                    commission_amount: choice.value === 'percent' ? '' : data.commission_amount,
                  };
                  setData(prev => ({ ...prev, ...next }));
                  setTimeout(() => handleAutoAdvance(next), 100);
                }}
              >
                {choice.label}
              </button>
            ))}
          </div>
        );

      case 'yes_no':
        return (
          <div className="lw-commission">
            {['yes', 'no'].map((choice) => (
              <button
                key={choice}
                className={`wizard-button ${data[q.id] === choice ? 'active' : ''}`}
                onClick={() => {
                  set(q.id, choice);
                  setTimeout(() => handleAutoAdvance({ [q.id]: choice }), 100);
                }}
              >
                {choice === 'yes' ? 'Yes' : 'No'}
              </button>
            ))}
          </div>
        );

      case 'days':
        return (
          <div className="lw-expiration">
            {['30', '45', '60', '90'].map((days) => (
              <button
                key={days}
                className={`wizard-button ${String(data[q.id]) === days ? 'active' : ''}`}
                onClick={() => {
                  set(q.id, days);
                  setTimeout(handleAutoAdvance, 100);
                }}
              >
                {days} days
              </button>
            ))}
            <div className="wizard-input-group">
              <input
                type="number"
                value={data[q.id] || ''}
                onChange={e => set(q.id, e.target.value)}
                placeholder="Other days"
                className="wizard-input"
                min="1"
              />
            </div>
          </div>
        );

      case 'optional_currency':
        return (
          <div className="lw-optional">
            <div className="wizard-currency">
              <span className="currency-symbol">$</span>
              <input
                type="number"
                value={data[q.id] || ''}
                onChange={e => set(q.id, e.target.value)}
                placeholder={q.placeholder}
                className="wizard-input currency-input"
                autoFocus
              />
            </div>
            <button
              className="wizard-button lw-skip-btn"
              onClick={() => {
                set(q.id, '');
                handleAutoAdvance();
              }}
            >
              {q.skipLabel}
            </button>
          </div>
        );

      case 'optional_text':
        return (
          <div className="lw-optional">
            <input
              type="text"
              value={data[q.id] || ''}
              onChange={e => set(q.id, e.target.value)}
              placeholder={q.placeholder}
              className="wizard-input"
              autoFocus
            />
            <button
              className="wizard-button lw-skip-btn"
              onClick={() => {
                set(q.id, '');
                handleAutoAdvance();
              }}
            >
              {q.skipLabel}
            </button>
          </div>
        );

      case 'optional_number':
        return (
          <div className="lw-optional">
            <input
              type="number"
              value={data[q.id] || ''}
              onChange={e => set(q.id, e.target.value)}
              placeholder={q.placeholder}
              className="wizard-input"
              autoFocus
            />
            <button
              className="wizard-button lw-skip-btn"
              onClick={() => {
                set(q.id, '');
                handleAutoAdvance();
              }}
            >
              {q.skipLabel}
            </button>
          </div>
        );

      case 'expiration':
        return (
          <div className="lw-expiration">
            <button
              className={`wizard-button ${data.expiration_date === inDays(90) ? 'active' : ''}`}
              onClick={() => {
                set('expiration_date', inDays(90));
                setTimeout(handleAutoAdvance, 100);
              }}
            >
              90 days ({toMMDDYYYY(inDays(90))})
            </button>
            <div className="wizard-input-group">
              <input
                type="date"
                value={data.expiration_date || ''}
                onChange={e => set('expiration_date', e.target.value)}
                className="wizard-input"
                min={today()}
              />
            </div>
          </div>
        );

      case 'commission':
        return (
          <div className="lw-commission">
            {['2', '2.5', '3', '3.5'].map(pct => (
              <button
                key={pct}
                className={`wizard-button ${data.commission_percent === pct ? 'active' : ''}`}
                onClick={() => {
                  set('commission_percent', pct);
                  setTimeout(handleAutoAdvance, 100);
                }}
              >
                {pct}%
              </button>
            ))}
            <div className="wizard-input-group">
              <input
                type="number"
                value={data.commission_percent || ''}
                onChange={e => set('commission_percent', e.target.value)}
                placeholder="Other %"
                className="wizard-input"
                step="0.25"
                min="0"
                max="10"
              />
            </div>
          </div>
        );

      case 'seller':
        return (
          <div className="lw-seller">
            {sellers.map((seller, idx) => (
              idx === 0 ? (
                <React.Fragment key={idx}>
                  {sellers.length > 1 && (
                    <div className="lw-seller-header">Seller #1</div>
                  )}
                  <input
                    type="text"
                    value={seller.name}
                    onChange={e => updateSeller(idx, 'name', e.target.value)}
                    placeholder="Full name"
                    className="wizard-input"
                    autoFocus
                  />
                  <input
                    type="email"
                    value={seller.email}
                    onChange={e => updateSeller(idx, 'email', e.target.value)}
                    placeholder="Email (optional)"
                    className="wizard-input"
                  />
                  <PhoneNumberInput
                    value={seller.phone}
                    onChange={e => updateSeller(idx, 'phone', e.target.value)}
                    placeholder="e.g. 405-555-1234"
                    className="wizard-input"
                  />
                </React.Fragment>
              ) : (
                <div key={idx} className="lw-coseller">
                  <div className="lw-coseller-header">
                    <span>Seller #{idx + 1}</span>
                    <button className="lw-remove-btn" onClick={() => removeSeller(idx)}>
                      Remove
                    </button>
                  </div>
                  <input
                    type="text"
                    value={seller.name}
                    onChange={e => updateSeller(idx, 'name', e.target.value)}
                    placeholder="Full name"
                    className="wizard-input"
                  />
                  <input
                    type="email"
                    value={seller.email}
                    onChange={e => updateSeller(idx, 'email', e.target.value)}
                    placeholder="Email (optional)"
                    className="wizard-input"
                  />
                  <PhoneNumberInput
                    value={seller.phone}
                    onChange={e => updateSeller(idx, 'phone', e.target.value)}
                    placeholder="e.g. 405-555-1234"
                    className="wizard-input"
                  />
                </div>
              )
            ))}
            {sellers.length < 3 && (
              <button className="wizard-button lw-add-coseller-btn" onClick={addSeller}>
                + Add Seller
              </button>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  if (!visible) return null;

  // Success / error screen
  const overlayClass = isPageMode ? 'wizard-overlay wizard-page-mode' : 'wizard-overlay';

  if (submitResult) {
    return (
      <div className={overlayClass}>
        <div className="wizard-container lw-container">
          <div className="wizard-header">
            <h2>New Listing</h2>
            <button
              className="wizard-close"
              onClick={() => onClose(submitResult.success ? { listingId: submitResult.listingId } : undefined)}
            >×</button>
          </div>
          <div className="wizard-content">
            {submitResult.success ? (
              <>
                <div className="result-message success">
                  ✅ Listing created successfully!
                </div>
                <div className="result-details">
                  <p><strong>Property:</strong> {submitResult.address}</p>
                  <p><strong>KeyFlo Listing ID:</strong> {submitResult.listingId}</p>
                  {submitResult.syncMessage && (
                    <p className="lw-sync-note">{submitResult.syncMessage}</p>
                  )}
                </div>
              </>
            ) : (
              <div className="result-message error">
                ❌ {submitResult.message}
              </div>
            )}
            <div className="wizard-actions">
              <button
                className="wizard-button primary"
                onClick={() => onClose(submitResult.success ? { listingId: submitResult.listingId } : undefined)}
              >
                {submitResult.success ? 'View listing' : 'Close'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Submitting overlay
  if (isSubmitting) {
    return (
      <div className={overlayClass}>
        <div className="wizard-container lw-container">
          <div className="wizard-header">
            <h2>New Listing</h2>
          </div>
          <div className="wizard-content lw-submitting">
            <div className="lw-spinner" />
            <p className="lw-submitting-text">
              {submitPhase === 'creating' && 'Creating listing in KeyFlo…'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  const stepLabel = visibleListingStepLabel(questions, currentStep, data);
  const actionButtons = (
    <>
      <button
        className="wizard-button secondary"
        onClick={handlePrevious}
        disabled={currentStep === 0 && !onBackToCatalog}
      >
        Previous
      </button>
      {isLastStep ? (
        <button
          className={WIZARD_PRIMARY_BUTTON_CLASS}
          onClick={handleSubmit}
          disabled={!canProceed() || isSubmitting}
        >
          Create Listing
        </button>
      ) : (
        <button
          className={WIZARD_PRIMARY_BUTTON_CLASS}
          onClick={handleNext}
          disabled={(currentQuestion.required && !canProceed()) || propertyEnriching}
        >
          {propertyEnriching ? 'Looking up property…' : 'Next'}
        </button>
      )}
    </>
  );

  return (
    <div className={overlayClass}>
      <div className="wizard-container lw-container">
        <div className="wizard-header">
          <h2>New Listing</h2>
          <button className="wizard-close" onClick={onClose}>×</button>
        </div>

        <WizardProgress current={stepLabel.visible} total={stepLabel.total} />

        <div className="wizard-content">
          <WizardStepReady ready={stepSeeded}>
            <div className="wizard-question">
              <h3 style={{ whiteSpace: 'pre-line' }}>{currentQuestion.question}</h3>
              {currentQuestion.id === 'additional_inclusions' && data.property_features && (
                <p className="wizard-property-features">
                  RentCast listed features (display only): {data.property_features}
                </p>
              )}
              {renderInput()}
            </div>

            {!isPageMode && (
              <div className="wizard-actions">{actionButtons}</div>
            )}
          </WizardStepReady>
        </div>
        {isPageMode && stepSeeded && (
          <div className="wizard-sticky-footer">
            {actionButtons}
          </div>
        )}
      </div>
    </div>
  );
};

export default ListingWizard;
